import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { DEFAULT_SITE_CONTENT, SITE_CONTENT_SECTIONS } from '../data/siteContent';

const SiteContentContext = createContext({ content: DEFAULT_SITE_CONTENT, reload: () => {} });

export function useSiteContent() {
  return useContext(SiteContentContext);
}

// Each section the admin saved replaces its default; anything missing or malformed keeps the default
export function mergeSiteContent(stored) {
  const merged = { ...DEFAULT_SITE_CONTENT };
  if (!stored) return merged;
  SITE_CONTENT_SECTIONS.forEach((key) => {
    const value = stored[key];
    if (value === undefined || value === null) return;
    if (key === 'home_faq' || key === 'faq') {
      if (Array.isArray(value)) merged[key] = value;
    } else if (key === 'playbook_descriptions') {
      // Blank descriptions fall back to the default text
      const filled = Object.fromEntries(Object.entries(value).filter(([, text]) => typeof text === 'string' && text.trim()));
      merged[key] = { ...DEFAULT_SITE_CONTENT.playbook_descriptions, ...filled };
    } else if (typeof value === 'object') {
      merged[key] = { ...DEFAULT_SITE_CONTENT[key], ...value };
    }
  });
  return merged;
}

// Loads Firestore site_content/main once; the defaults show until it arrives or if it can't be read
export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_SITE_CONTENT);

  const reload = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, 'site_content', 'main'));
      setContent(mergeSiteContent(snap.exists() ? snap.data() : null));
    } catch (error) {
      setContent(DEFAULT_SITE_CONTENT);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <SiteContentContext.Provider value={{ content, reload }}>
      {children}
    </SiteContentContext.Provider>
  );
}
